import hamburgerMenuAnimationDark from '@assets/animations/hamburger-menu-dark.json';
import hamburgerMenuAnimation from '@assets/animations/hamburger-menu.json';
import LogOutBtn from '@components/atoms/navbar/LogOutBtn';
import NavBarCta from '@components/molecules/navbar/NavBarCta';
import { useTheme } from '@hooks/useTheme';
import { useProfile } from '@hooks/api';
import { useLoadingModal } from '@hooks/useLoadingModal';
import { cn } from '@moreharvest/component-library';
import { navClick } from '@services/analytics';
import {
    AnalyticsDestination,
    AnalyticsNavLocation,
    AnalyticsScreenName,
} from 'mh-client';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { useContext, useEffect, useRef, useState } from 'react';
import NavLinks from '../../molecules/navbar/NavLinks';
import NavLogo from '../../molecules/navbar/NavLogo';
import ContextMenu from './ContextMenu';
import { HamburgerMenuContext } from './HamburgerMenuContext';

const getNavLinksWrapperClasses = (isOpen: boolean) =>
    cn(
        'flex flex-col w-full pt-16 absolute bg-neutral-50 dark:bg-neutral-50-dark top-0 z-20 px-4 pb-4 h-screen overflow-y-scroll',
        'transition-all duration-300 ease-in-out',
        isOpen ? 'translate-x-0' : 'translate-x-full'
    );

const MobileNavBar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { data: userProfile, isLoading } = useProfile();

    useLoadingModal(isLoading, 'MobileNavBar');

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [isOpen]);

    return userProfile ? (
        <HamburgerMenuContext.Provider value={{ isOpen, setIsOpen }}>
            <div className="relative lg:hidden  w-full mb-6">
                <div className={getNavLinksWrapperClasses(isOpen)}>
                    <NavBarCta accountStatus={userProfile?.accountStatus} />
                    <div className="w-full py-8 border-b-1 border-neutral-400 dark:border-neutral-400-dark">
                        <ContextMenu cytag="mobile-nav" />
                    </div>
                    <div className="pt-8 mb-10">
                        <NavLinks cytag="mobile-nav" />

                        <div className="mt-8">
                            <LogOutBtn
                                screenName={
                                    AnalyticsScreenName.getInstance().MENU
                                }
                            />
                        </div>
                    </div>
                </div>
                <div className="fixed flex top-0 left-0 justify-between px-4 py-1 items-center w-full bg-base-white dark:bg-base-black z-[60]">
                    <NavLogo />
                    <HamburgerIcon />
                </div>
            </div>
        </HamburgerMenuContext.Provider>
    ) : (
        <></>
    );
};

export default MobileNavBar;

const HamburgerIcon = () => {
    const { setIsOpen, isOpen } = useContext(HamburgerMenuContext);
    const [, , isDarkMode] = useTheme();
    const lottieRef = useRef<LottieRefCurrentProps>(null);
    const firstLoad = useRef(true);

    useEffect(() => {
        if (firstLoad.current) {
            firstLoad.current = false;
            return;
        }
        if (lottieRef.current && !firstLoad.current) {
            lottieRef.current.playSegments(isOpen ? [0, 48] : [48, 96], true);
        }
    }, [isOpen]);

    return (
        <button
            onClick={() => {
                if (!isOpen) {
                    navClick({
                        nav_location:
                            AnalyticsNavLocation.getInstance().SIDE_MENU,
                        destination: AnalyticsDestination.getInstance().MENU,
                    });
                }
                setIsOpen(!isOpen);
            }}
            className="w-12 h-12 flex items-center justify-center"
            data-cy={isOpen ? 'mobile-nav-close' : 'mobile-nav-hamburger'}
        >
            {isDarkMode ? (
                <Lottie
                    lottieRef={lottieRef}
                    loop={false}
                    autoPlay={false}
                    animationData={hamburgerMenuAnimationDark}
                    height={48}
                    width={48}
                    initialSegment={isOpen ? [48, 48] : [96, 96]}
                />
            ) : (
                <Lottie
                    lottieRef={lottieRef}
                    loop={false}
                    autoPlay={false}
                    animationData={hamburgerMenuAnimation}
                    height={48}
                    width={48}
                    initialSegment={isOpen ? [48, 48] : [96, 96]}
                />
            )}
        </button>
    );
};
